/*
  Statistische Kennzahlen für Mitarbeitende im Shiftplan V2.
*/

import { getMonthDays, parseDateValue, toIsoDate } from "../date/month";
import { getShiftDayKeys } from "./selectors";
import { countUserHolidayDays } from "./userHolidays";
import { buildEmployeeWorkloadMap } from "./workload";

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("de-CH", { month: "short" });
const MONTH_LONG_LABEL_FORMATTER = new Intl.DateTimeFormat("de-CH", { month: "long" });

function buildYearDays(year) {
  const normalizedYear = Number(year) || new Date().getFullYear();
  const days = [];

  for (
    let cursor = new Date(normalizedYear, 0, 1);
    cursor.getFullYear() === normalizedYear;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
  ) {
    days.push({
      key: toIsoDate(cursor),
      date: cursor,
    });
  }

  return days;
}

function filterShiftsByEmployeeAndDays(shifts, employeeId, days) {
  const employeeKey = String(employeeId || "");
  const dayKeys = new Set((Array.isArray(days) ? days : []).map((day) => String(day?.key || "")).filter(Boolean));

  return (Array.isArray(shifts) ? shifts : []).filter(
    (shift) =>
      String(shift?.employeeId || "") === employeeKey &&
      getShiftDayKeys(shift).some((dateKey) => dayKeys.has(dateKey))
  );
}

function getEmployeeWorkload(employee, shifts, days, workingDaysPerWeek, absenceOptions, vacationAbsenceIds, userHolidays) {
  const workloadMap = buildEmployeeWorkloadMap({
    employees: employee ? [employee] : [],
    shifts,
    days,
    workingDaysPerWeek,
    absenceOptions,
    vacationAbsenceIds,
    userHolidays,
  });

  return workloadMap[String(employee?.id || "")] || {
    plannedHours: 0,
    targetHours: 0,
    vacationDays: 0,
  };
}

function countDistinctDays(shifts, days, predicate) {
  const visibleDayKeys = new Set((Array.isArray(days) ? days : []).map((day) => String(day?.key || "")).filter(Boolean));
  const dateKeys = new Set();

  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    if (!predicate(shift)) return;
    getShiftDayKeys(shift).forEach((dateKey) => {
      if (!visibleDayKeys.has(dateKey)) return;
      dateKeys.add(dateKey);
    });
  });

  return dateKeys.size;
}

function getUtilizationPercent(plannedHours, targetHours) {
  const planned = Number(plannedHours || 0);
  const target = Number(targetHours || 0);
  if (!target || target <= 0) return 0;

  return Math.max(0, Math.round((planned / target) * 100));
}

function buildPeriodStats({ employee, shifts, days, workingDaysPerWeek, absenceOptions, vacationAbsenceIds, userHolidays }) {
  const periodShifts = filterShiftsByEmployeeAndDays(shifts, employee?.id, days);
  const workload = getEmployeeWorkload(employee, periodShifts, days, workingDaysPerWeek, absenceOptions, vacationAbsenceIds, userHolidays);
  const legacyVacationDayCount = countDistinctDays(
    periodShifts,
    days,
    (shift) => shift?.entryType === "absence" && vacationAbsenceIds.has(String(shift?.absenceId || ""))
  );
  const userHolidayDayCount = countUserHolidayDays({ holidays: userHolidays, employeeId: employee?.id, days, workingDaysPerWeek });
  const vacationDayCount = Math.max(legacyVacationDayCount, userHolidayDayCount);
  const absenceDayCount = countDistinctDays(periodShifts, days, (shift) => shift?.entryType === "absence");
  const nonVacationAbsenceDayCount = countDistinctDays(
    periodShifts,
    days,
    (shift) => shift?.entryType === "absence" && !vacationAbsenceIds.has(String(shift?.absenceId || ""))
  );
  const workDayCount = countDistinctDays(periodShifts, days, (shift) => shift?.entryType !== "absence");
  const draftCount = periodShifts.filter((shift) => shift?.isDraft).length;

  return {
    plannedHours: workload.plannedHours || 0,
    targetHours: workload.targetHours || 0,
    balanceHours: (workload.plannedHours || 0) - (workload.targetHours || 0),
    utilizationPercent: getUtilizationPercent(workload.plannedHours, workload.targetHours),
    workDays: workDayCount,
    shiftCount: periodShifts.filter((shift) => shift?.entryType !== "absence").length,
    vacationDays: vacationDayCount,
    absenceDays: absenceDayCount,
    nonVacationAbsenceDays: nonVacationAbsenceDayCount,
    draftCount,
  };
}

export function buildEmployeeStatsModel({
  employee,
  shifts,
  monthDate,
  workingDaysPerWeek,
  absenceOptions = [],
  vacationAbsenceIds = new Set(),
  userHolidays = [],
}) {
  const selectedMonthDate = parseDateValue(monthDate) || new Date();
  const year = selectedMonthDate.getFullYear();
  const yearDays = buildYearDays(year);
  const monthDays = getMonthDays(selectedMonthDate);
  const normalizedVacationIds =
    vacationAbsenceIds instanceof Set ? vacationAbsenceIds : new Set(Array.from(vacationAbsenceIds || []));

  const yearStats = buildPeriodStats({
    employee,
    shifts,
    days: yearDays,
    workingDaysPerWeek,
    absenceOptions,
    vacationAbsenceIds: normalizedVacationIds,
    userHolidays,
  });
  const monthStats = buildPeriodStats({
    employee,
    shifts,
    days: monthDays,
    workingDaysPerWeek,
    absenceOptions,
    vacationAbsenceIds: normalizedVacationIds,
    userHolidays,
  });
  const monthlySeries = Array.from({ length: 12 }, (_item, monthIndex) => {
    const date = new Date(year, monthIndex, 1);
    const stats = buildPeriodStats({
      employee,
      shifts,
      days: getMonthDays(date),
      workingDaysPerWeek,
      absenceOptions,
      vacationAbsenceIds: normalizedVacationIds,
      userHolidays,
    });

    return {
      monthIndex,
      label: MONTH_LABEL_FORMATTER.format(date).replace(".", ""),
      fullLabel: MONTH_LONG_LABEL_FORMATTER.format(date),
      ...stats,
    };
  });

  return {
    employee,
    calendarYear: year,
    monthDate: selectedMonthDate,
    monthIndex: selectedMonthDate.getMonth(),
    year: yearStats,
    month: monthStats,
    monthlySeries,
  };
}
